<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { TopDialog, TopDialogPage, useTopDialogSelf } from '@/components/dialog/dialog';
import { genFlagLinkByCountryCode } from '@/core/utils/string';
import type { Props, RegionRich } from './types';
import { getDeviceGIcon, getLangLabel, getSearcherGIcon } from '@/core/utils/searchers';
import Core from '@/core/core/core';

defineOptions({ id: 'selectorRegions' });

const props = defineProps<Partial<Props>>();

const dialog = useTopDialogSelf();

/**
 * Список расширенных регионов для использования в компоненте
 */
const regionsRich = ref<RegionRich[]>([]);

/**
 * Ссылка на элемент с регионами
 */
const elRegions = ref<null | HTMLElement>(null);

/**
 * Направление сортировки по полю
 */
const sortedVectorByField = reactive({
	searcher_key: -1,
	key: -1,
	name: -1,
});

/**
 * Выбраны все регионы
 */
const isAllSelected = computed({
	get() {
		return regionsRich.value.every(region => region.selected);
	},

	set(value) {
		regionsRich.value = regionsRich.value.map(region => ({ ...region, selected: value }));
	},
});

/**
 * Сортировать регионы по выбранному полю
 * @param sortedField - Поле для сортировки
 */
const sort = (sortedField: keyof typeof sortedVectorByField) => {
	sortedVectorByField[sortedField] = -sortedVectorByField[sortedField];

	regionsRich.value.sort((regionA, regionB) => {
		if (!regionA[sortedField] || !regionB[sortedField]) return -1;

		return regionA[sortedField] >= regionB[sortedField] ? sortedVectorByField[sortedField] : -sortedVectorByField[sortedField];
	});
};

/**
 * Применить изменения
 *
 * Вызывает callback `props['@update:regionsIndexes']`
 */
const onSubmit = () => {
	if (!regionsRich.value.some(region => region.selected)) return;

	const regionsIndexes = regionsRich.value
		.filter(region => region.selected)
		.map(region => region.index);

	props['@update:regionsIndexes']?.(regionsIndexes);

	dialog.close();
};

// генерация регионов
watch([
	() => props.regions,
	() => props.regionsIndexes,
], () => {
	regionsRich.value = [];

	if (!props.regions) return;
	if (!props.regionsIndexes) return;

	let regions = [...props.regions];

	// отсортировать регионы по входным данным
	const orderByRegionIndex = new Map(props.regionsIndexes.map((index, order) => [index, order]));

	regions.sort((regionA, regionB) => {
		const orderA = orderByRegionIndex.get(regionA.index) ?? Number.MAX_VALUE;
		const orderB = orderByRegionIndex.get(regionB.index) ?? Number.MAX_VALUE;

		return orderA - orderB;
	});

	regions.forEach(region => {
		let title = region.name;
		if (region.areaName) title += ` (${region.areaName})`;

		const selected = props.regionsIndexes?.includes(region.index) ?? false;
		const searcherIcon = getSearcherGIcon(region.searcher_key);
		const langLabel = region.lang !== undefined ? getLangLabel(region.searcher_key ?? 999, region.lang) : undefined;
		const deviceIcon = region.device !== undefined ? getDeviceGIcon(region.device) : undefined;
		const flagLink = region.countryCode !== undefined ? genFlagLinkByCountryCode(region.countryCode) : undefined;

		const regionRich = {
			...region,
			selected,
			title,
			searcherIcon,
			langLabel,
			deviceIcon,
			flagLink,
		};

		regionsRich.value.push(regionRich);
	});
});

/**
 * Проверить на существование sortable
 */
const onOpen = () => {
	if (!Core.$?.ui['sortable']) {
		console.info('Для работы сортировки требуется глобальная загрузка jQuery UI Sortable');

		return;
	}

	if (!elRegions.value) return;

	$(elRegions.value).sortable({
		distance: 10,

		stop: () => {
			// Обновляем порядок регионов в массиве regionsRich после сортировки
			const sortedIndexes = [...elRegions.value!.children].map(el => Number(el.getAttribute('data-region-index')));
			regionsRich.value.sort((a, b) => sortedIndexes.indexOf(a.index) - sortedIndexes.indexOf(b.index));
		},
	});
};

/**
 * Удаление sortable
 */
const onClose = () => {
	if (!Core.$?.ui['sortable']) return;
	if (!elRegions.value) return;

	$(elRegions.value).sortable('destroy');
};

</script>

<template>
	<TopDialog
		:id="dialog.id"
		pageActive="selectorRegions"
		class="dialog_selectorRegions"
		height="600px"
		width="600px"
		@open="onOpen"
		@close="onClose"
	>
		<TopDialogPage name="selectorRegions" :order="0">
			<template #header>
				<div class="dialog_selectorRegions_header">
					<TopCheckbox v-model="isAllSelected"></TopCheckbox>

					<TopButton
						class="dialog_selectorRegions_sortButton"
						color="theme"
						data-top-icon=""
						@click="sort('searcher_key')"
						:title="$i18n.Projects?.Sort_by_search_engine"
					/>

					<TopButton
						class="dialog_selectorRegions_sortButton"
						color="theme"
						data-top-icon=""
						@click="sort('key')"
						:title="$i18n.Projects?.Sort_by_yande_region_code"
					/>

					<TopButton
						class="dialog_selectorRegions_sortButton"
						color="theme"
						data-top-icon=""
						@click="sort('name')"
						:title="$i18n.Projects?.Sort_by_lphabetically"
					/>
				</div>
			</template>

			<template #body>
				<div ref="elRegions">
					<TopCheckbox
						v-for="region of regionsRich"
						:key="region.index"
						v-model="region.selected"
						class="dialog_selectorRegions_region"
						:data-region-index="region.index"
					>
						<div
							class="dialog_selectorRegions_regionSearcherIcon"
							:data-top-icon="region.searcherIcon"
						></div>

						<img
							v-if="region.flagLink"
							class="top-flag"
							:src="'//topvisor.dev' + region.flagLink"
							:alt="region.countryCode"
						>

						<div class="top-ellipsis">
							{{ region.name }}

							<span class="dialog_selectorRegions_regionAreaName">
								{{ region.areaName }}
							</span>
						</div>

						<div v-if="region.langLabel">
							[{{ region.langLabel }}]
						</div>

						<div
							v-if="region.deviceIcon"
							:data-top-icon="region.deviceIcon"
						></div>
					</TopCheckbox>
				</div>
			</template>

			<template #footer>
				<TopButton @click="onSubmit">
					{{ $i18n.Common?.Apply }}
				</TopButton>
			</template>
		</TopDialogPage>
	</TopDialog>
</template>

<style>
@import "./style.css";
</style>
